import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Button, Stack, Heading, Box, Text, Divider, Inline } from '@machobear/design-system'
import config from '@/config'
import { breakpoints } from '@/styles'
import { isNullOrEmpty } from '@/utils'
import useMediaQuery from '@/hooks/use-media-query'
import useProfile from '@/hooks/use-profile'
import useRole from '@/hooks/use-role'
import CatTooltip from '@/components/cat-tooltip'
import VerifiedBadge from '@/components/verified-badge'
import Skeleton from '@/components/skeleton'
import {
  personaEmploymentVerificationClassified,
  personaEmploymentVerificationUnclassified,
  personaIncomeVerificationClassified,
  personaIncomeVerificationUnclassified
} from '@/features/Persona'
import { PanelContainer as Panel } from '@/features/Settings/elements'
import { MenuItem, Label, Description } from './menu-item'

const PreferenceWrapper = styled.div`
  position: relative;
  margin-bottom: 50px;
`

const PanelContainer = styled(Panel)`
  position: relative;
  z-index: 1;

  > :last-child {
    > div {
      border: none;
    }
  }
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-top: 32px;
    padding: 10px;
    max-width: none;
    border: none;
    box-shadow: none;
  }
`

const VerifyWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
`

const StyledLabel = styled(Label)`
  display: flex;
  align-items: center;
`

const ButtonWrapper = styled.div`
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: 100%;
  }
`

const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 22px 0 !important;
`

const DashboardItems = ({ completedProfile }) => {
  const { t, i18n } = useTranslation()
  const language = i18n.languages[0]
  const profile = useProfile()
  const isLoadingProfile = isNullOrEmpty(profile.id)
  const identityVerified = profile.identity_verified_state === 'verified'
  const identityVerificationPending = profile.identity_verified_state === 'pending'
  const phoneVerified = !isNullOrEmpty(profile.phone_number)
  const disableButton = phoneVerified && (identityVerificationPending || identityVerified)
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const role = useRole()
  const isTenant = role === 'tenant'
  const isProd = config.BUILD_ENV === 'prod'

  return (
    <PreferenceWrapper>
      {isLoadingProfile ? (
        <PanelContainer vSpace={20} hSpace={60}>
          <SkeletonWrapper>
            <Skeleton height={52} width="100%" borderRadius={8} />
            <Skeleton height={1} width="100%" />
            <Skeleton height={52} width="100%" borderRadius={8} />
            <Skeleton height={1} width="100%" />
            <Skeleton height={52} width="100%" borderRadius={8} />
          </SkeletonWrapper>
        </PanelContainer>
      ) : (
        <>
          <PanelContainer vSpace={20} hSpace={60}>
            {!identityVerified && (
              <MenuItem to="/profile/verify" disabled={disableButton}>
                <VerifyWrapper>
                  <Box>
                    <StyledLabel>
                      {t('b.settings.view.become_trusted.label', 'Become Trusted')}
                      <VerifiedBadge type="id" width={30} height={22} verified />
                    </StyledLabel>
                    <Description>
                      {t(
                        'b.settings.view.become_trusted_tenant.description',
                        'Verify phone number and ID to improve your credibility.'
                      )}
                    </Description>
                  </Box>
                  <ButtonWrapper aria-hidden="true">
                    <Button disabled={disableButton} tabIndex="-1">
                      {identityVerificationPending
                        ? t('b.settings.view.verifying.text', 'Verifying...')
                        : t('b.settings.view.verify_now.text', 'Verify Now')}
                    </Button>
                  </ButtonWrapper>
                </VerifyWrapper>
              </MenuItem>
            )}
            <MenuItem to="/profile/edit">
              <Label>{t('b.settings.view.basic_information.label', 'Basic Information')}</Label>
              <Description>
                {t(
                  'b.settings.view.basic_information.description',
                  'Profile photo, about yourself, address...'
                )}
              </Description>
            </MenuItem>
            <MenuItem to="/settings/preferences">
              <Label>{t('b.settings.view.preferences.label', 'Preferences')}</Label>
              <Description>
                {t(
                  'b.settings.view.preferences.description',
                  'Measurement units, language settings...'
                )}
              </Description>
            </MenuItem>
            <MenuItem
              to={
                isMobile
                  ? '/settings'
                  : isTenant
                    ? '/settings/payment-settings'
                    : '/settings/plan-settings'
              }
            >
              <Label>{t('b.settings.view.account_settings.label', 'Account Settings')}</Label>
              <Description>
                {t(
                  'b.settings.view.account_settings.description',
                  'Payment settings, change password...'
                )}
              </Description>
            </MenuItem>
          </PanelContainer>
          {!completedProfile && (
            <CatTooltip
              message={t(
                'r.profile.view.profile_completion_tooltip.text',
                `Did you know? You don't have to fill out everything now. Just do it before submitting an application!`
              )}
            />
          )}
        </>
      )}

      {/* PERSONA TEMPLATE TESTING - DEV ONLY */}
      {!isProd && (
        <Box marginTop="xlarge">
          <PanelContainer vSpace={40} hSpace={60}>
            <Box padding={{ mobile: 'small', tablet: 'none' }}>
              <Stack space="large">
                <Box paddingTop={{ mobile: 'gutter', tablet: 'none' }}>
                  <Heading level="6">DEV ONLY - Persona Testing</Heading>
                </Box>
                <Stack space="gutter">
                  <Divider />
                  <Text>Persona Employment Verification</Text>
                  <Inline>
                    <Button
                      type="button"
                      onClick={() =>
                        personaEmploymentVerificationClassified({
                          id: profile.id,
                          firstName: profile.first_name,
                          lastName: profile.last_name,
                          language
                        })
                      }
                    >
                      Open Classified Template
                    </Button>
                  </Inline>
                  <Inline>
                    <Button
                      type="button"
                      onClick={() =>
                        personaEmploymentVerificationUnclassified({
                          id: profile.id,
                          firstName: profile.first_name,
                          lastName: profile.last_name,
                          language
                        })
                      }
                    >
                      Open Unclassified Template
                    </Button>
                  </Inline>
                  <Divider />
                  <Text>Persona Income Verification</Text>
                  <Inline>
                    <Button
                      type="button"
                      onClick={() =>
                        personaIncomeVerificationClassified({
                          id: profile.id,
                          firstName: profile.first_name,
                          lastName: profile.last_name,
                          language
                        })
                      }
                    >
                      Open Classified Template
                    </Button>
                  </Inline>
                  <Inline>
                    <Button
                      type="button"
                      onClick={() =>
                        personaIncomeVerificationUnclassified({
                          id: profile.id,
                          firstName: profile.first_name,
                          lastName: profile.last_name,
                          language
                        })
                      }
                    >
                      Open Unclassified Template
                    </Button>
                  </Inline>
                  <Box paddingTop={{ mobile: 'small', tablet: 'none' }}></Box>
                </Stack>
              </Stack>
            </Box>
          </PanelContainer>
        </Box>
      )}
    </PreferenceWrapper>
  )
}

export default DashboardItems
